// Stateless pre-draw/row identity helpers, safe to share across browser bundles.
import { MAX_TABLE_CHAIN, RANDOM_TABLE_KINDS, type RandomTableDef } from './random-table';

type Row = Record<string, unknown>;
export function selectRollReveal(rows: Iterable<Row>, rollId: string): Row | null {
    let selected: Row | null = null;
    for (const row of rows) {
        if (String(row.roll_id ?? '').toUpperCase() !== rollId || Number(row.roll_private ?? 0) === 0) continue;
        if (!selected || Number(row.roll_i) < Number(selected.roll_i)) selected = row;
    }
    return selected;
}

// Walk possible attachments before spending entropy. The native resolver still chooses the
// range/quantity and builds each row; this only hydrates its closure and catches chain limits.
export async function prepareTableRoll(
    id: string, read: (id: string) => RandomTableDef | null,
    load: (id: string) => Promise<RandomTableDef | null>,
): Promise<string | null> {
    const visited = new Map<string, number>();
    async function visit(key: string, depth: number): Promise<string | null> {
        const def = read(key) ?? await load(key);
        if (!def) return 'unavailable';
        if (def.rollType !== null) return 'unsupported:roll_type';
        if ((visited.get(key) ?? 0) >= depth) return null;
        visited.set(key, depth);
        if (def.authored && def.pools.length === 0) return null;
        for (const range of def.ranges) {
            const type = range.attached_type ?? '';
            const addsTable = RANDOM_TABLE_KINDS.has(type);
            const addsQuantity = type === 'creature' && (range.qty_dice ?? 0) > 0 && (range.qty_die ?? 0) > 0;
            if (!range.attached || (!addsTable && !addsQuantity)) continue;
            if (range.attached === key && depth > 1) continue;
            if (depth >= MAX_TABLE_CHAIN) return 'chain-too-long';
            if (addsTable) {
                const failure = await visit(range.attached, depth + 1);
                if (failure) return failure;
            }
        }
        return null;
    }
    return visit(id, 1);
}
